import type { ComponentType } from '@/types/schemas/workspace'
import type { LinkMode } from '../types'

/**
 * Which panel types may act on a workspace focus-date broadcast, given the
 * component type that produced it.
 *
 * Background: `focusDate` is a single workspace-wide value (see
 * `workspace-mosaic.tsx`), so every panel that reads the prop follows every
 * broadcast. The date rules the panels actually implement are narrower:
 *
 *   Rule 2 — the scanner row click is the canonical cross-panel date producer,
 *            and the panels it drives (charts, watchlist, multi-chart) are all
 *            meant to rewind with it.
 *   Rule 4 — that click also clears panel-local dates on linked targets, so the
 *            broadcast wins over a stale local override.
 *
 * A Ticker Stats **Records** row click is not that. It reaches the workspace
 * through the raw `onFocusDate` prop rather than `applyDateBroadcast`, so it
 * never forwards a ticker and never clears panel-local dates — it is a
 * chart-rewind gesture, not a workspace-wide date change. Left ungated it
 * silently pinned every scanner / watchlist / multi-chart to a past date and
 * disabled their auto-refresh.
 *
 * Sources listed here drive charts (and their own kind) only. Everything else
 * keeps its existing reach.
 */
// Typed as ReadonlySet<string> for lookup (PanelHeader carries the source as a
// plain string) while the literal stays checked against ComponentType.
const CHART_ONLY_SOURCES: ReadonlySet<string> = new Set<ComponentType>(['ticker-stats'])

/**
 * True when a panel of `componentType` should act on a broadcast produced by
 * `source`.
 *
 * - Unknown/absent source → permit. Keeps every pre-existing caller (and any
 *   surface that does not track a source yet) on its current behavior.
 * - Same component type as the source → permit. A producer's own kind always
 *   reflects the date it just published (mirrors `isAffectedByFocus` in
 *   `panel-header.tsx`).
 * - `chart` → permit. The single chart is the universal rewind target.
 */
export function followsFocusBroadcast(
  componentType: ComponentType | string,
  source: ComponentType | string | null | undefined,
): boolean {
  if (!source) return true
  if (componentType === source) return true
  if (componentType === 'chart') return true
  return !CHART_ONLY_SOURCES.has(source)
}

/**
 * The broadcast a panel of `componentType` should see: the date itself when the
 * source may drive it, otherwise null. Convenience wrapper so consumers can keep
 * writing `panelLocal ?? broadcast ?? null`.
 */
export function resolveFocusBroadcast(
  componentType: ComponentType | string,
  focusDate: string | null | undefined,
  source: ComponentType | string | null | undefined,
): string | null {
  if (!focusDate) return null
  return followsFocusBroadcast(componentType, source) ? focusDate : null
}

/**
 * Chart panels the header padlock keeps on their own day. Lock (link mode
 * `none`) cuts a panel off from its peers' tickers, and a record's date travels
 * with its ticker, so on these charts it shuts out the date broadcast too: a
 * locked chart must not jump to the day of a record it is not showing. Their
 * own calendar is panel-local and they never produce a broadcast, so nothing of
 * theirs is lost. These are the ChartGrid charts under a workspace header.
 */
const LOCK_HOLDS_DAY_TYPES: ReadonlySet<string> = new Set<ComponentType>([
  'chart',
  'history-chart',
  'backtest-trade-chart',
])

interface LinkedPanel {
  componentType: ComponentType | string
  linkMode?: LinkMode
}

/** True when the panel is locked and keeps its own day (see LOCK_HOLDS_DAY_TYPES). */
export function holdsDayWhenLocked(config: LinkedPanel | undefined): boolean {
  return config?.linkMode === 'none' && LOCK_HOLDS_DAY_TYPES.has(config.componentType)
}

/**
 * The broadcast a panel sees once its link mode applies: null for a chart that
 * holds its own day, the date itself otherwise. No config (a chart cell or
 * drill id) passes the date through.
 */
export function linkedFocusDate(
  config: LinkedPanel | undefined,
  focusDate: string | null | undefined,
): string | null {
  if (!focusDate) return null
  return holdsDayWhenLocked(config) ? null : focusDate
}
